<template>
  <b-modal :visible="visible" title="Informações do Cliente" @hide="onHide">
    <div>
      <!-- Alerta se possui reports -->
      <b-alert
        v-if="hasReports && !showReportForm"
        show
        variant="warning"
        class="mb-3 d-flex align-items-center"
      >
        <i class="bi bi-shield-exclamation me-2" style="font-size: 1.2rem"></i>
        <div class="flex-grow-1">
          <strong>Atenção:</strong>
          Este cliente possui
          <b-badge variant="danger" class="ms-1">
            {{ computedReportsCount }}
          </b-badge>
          {{ computedReportsCount === 1 ? 'report' : 'reports' }} registrado{{
            computedReportsCount === 1 ? '' : 's'
          }}.
        </div>
      </b-alert>

      <p class="info-tip">
        Dica: Toque duas vezes em qualquer informação para copiá-la.
      </p>

      <p>
        <i
          class="bi bi-exclamation-triangle-fill text-warning"
          style="font-size: 2rem"
        ></i>
        <strong class="ms-2">Nome:</strong>
        <span
          @dblclick="copyToClipboard(customerView.name)"
          @touchend="doubleTap('name', customerView.name)"
        >
          {{ customerView.name || 'Sem Informações' }}
        </span>
      </p>

      <p>
        <strong>Email:</strong>
        <span
          @dblclick="copyToClipboard(customerView.email)"
          @touchend="doubleTap('email', customerView.email)"
        >
          {{ customerView.email || 'Sem Informações' }}
        </span>
      </p>

      <p>
        <strong>Telefone:</strong>
        <span
          @dblclick="copyToClipboard(customerView.phone)"
          @touchend="doubleTap('phone', customerView.phone)"
        >
          {{ customerView.phone || 'Sem Informações' }}
        </span>
      </p>

      <p>
        <strong>Documento:</strong>
        <span
          @dblclick="copyToClipboard(customerView.document)"
          @touchend="doubleTap('document', customerView.document)"
        >
          {{ customerView.document || 'Sem Informações' }}
        </span>
      </p>

      <p class="d-flex align-items-center">
        <strong>Pontos Atuais:</strong>
        <span
          class="points-badge ms-2 px-2 py-1 bg-light border"
          @dblclick="copyToClipboard(totalPointsString)"
          @touchend="doubleTap('points', totalPointsString)"
        >
          {{ totalPoints }} / 10
        </span>
      </p>

      <!-- Header da seção de reports + botão -->
      <div v-if="isAdmin" class="points-admin-panel mb-3">
        <div class="d-flex flex-wrap align-items-center gap-2">
          <b-button
            size="sm"
            variant="outline-success"
            @click="startPointAdjustment('add')"
          >
            <span class="fas fa-plus mr-1"></span>
            Adicionar pontos
          </b-button>
          <b-button
            size="sm"
            variant="outline-danger"
            @click="startPointAdjustment('remove')"
          >
            <span class="fas fa-minus mr-1"></span>
            Remover pontos
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            @click="togglePointHistory"
          >
            <span class="fas fa-history mr-1"></span>
            {{ showPointHistory ? 'Ocultar histórico' : 'Ver histórico' }}
          </b-button>
        </div>

        <b-alert
          v-if="pointAdjustment.error"
          show
          variant="danger"
          class="mt-3 mb-0"
        >
          {{ pointAdjustment.error }}
        </b-alert>

        <div v-if="showPointAdjustmentForm" class="point-adjustment-form mt-3">
          <strong>
            {{
              pointAdjustment.mode === 'add'
                ? 'Adicionar pontos'
                : 'Remover pontos'
            }}
          </strong>

          <b-form-group label="Quantidade" class="mt-2 mb-2">
            <b-form-input
              v-model.number="pointAdjustment.amount"
              type="number"
              min="1"
              max="100"
              step="1"
            />
          </b-form-group>

          <div class="small text-muted mb-2">
            Saldo atual: {{ totalPoints }} — Saldo resultante:
            <strong :class="{ 'text-danger': projectedPointsBalance < 0 }">
              {{ projectedPointsBalance }}
            </strong>
          </div>

          <b-form-group label="Motivo" class="mb-2">
            <b-form-textarea
              v-model.trim="pointAdjustment.reason"
              maxlength="500"
              rows="3"
              placeholder="Informe o motivo do ajuste"
            />
            <small class="text-muted">
              Mínimo de 5 caracteres. O motivo ficará registrado na auditoria.
            </small>
          </b-form-group>

          <div class="d-flex justify-content-end gap-2">
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="pointAdjustment.loading"
              @click="cancelPointAdjustment"
            >
              Cancelar
            </b-button>
            <b-button
              size="sm"
              variant="primary"
              :disabled="!canConfirmPointAdjustment"
              @click="confirmPointAdjustment"
            >
              {{ pointAdjustment.loading ? 'Salvando...' : 'Confirmar ajuste' }}
            </b-button>
          </div>
        </div>

        <div v-if="showPointHistory" class="point-history mt-3">
          <div v-if="pointHistory.loading" class="text-muted small">
            Carregando histórico...
          </div>
          <b-alert v-else-if="pointHistory.error" show variant="danger">
            {{ pointHistory.error }}
          </b-alert>
          <div v-else-if="!pointHistory.items.length" class="text-muted small">
            Nenhum ajuste manual registrado.
          </div>
          <b-list-group v-else>
            <b-list-group-item
              v-for="adjustment in pointHistory.items"
              :key="adjustment.id"
              class="point-history-item"
            >
              <div class="d-flex justify-content-between align-items-start">
                <strong
                  :class="adjustment.delta > 0 ? 'text-success' : 'text-danger'"
                >
                  {{ adjustment.delta > 0 ? '+' : '' }}{{ adjustment.delta }}
                  ponto(s)
                </strong>
                <small class="text-muted">
                  {{ formatDate(adjustment.createdAt) }}
                </small>
              </div>
              <div class="small">
                {{ adjustment.balanceBefore }} → {{ adjustment.balanceAfter }}
              </div>
              <div>{{ adjustment.reason }}</div>
              <small class="text-muted">
                Por:
                {{
                  (adjustment.createdByUser && adjustment.createdByUser.name) ||
                  'Administrador não identificado'
                }}
              </small>
            </b-list-group-item>
          </b-list-group>
        </div>
      </div>

      <div class="d-flex align-items-center justify-content-between mb-2">
        <div class="d-flex align-items-center">
          <strong>Reports:</strong>
          <b-badge :variant="hasReports ? 'danger' : 'secondary'" class="ms-2">
            {{ computedReportsCount }}
          </b-badge>
        </div>

        <b-button
          size="sm"
          :variant="showReportForm ? 'outline-danger' : 'outline-dark'"
          @click="toggleReportForm"
        >
          <i class="bi bi-flag me-1"></i>
          {{ showReportForm ? 'Cancelar' : 'Reportar cliente' }}
        </b-button>
      </div>

      <!-- Área dinâmica: OU lista de reports (motivos) OU formulário para novo report -->
      <transition name="fade">
        <!-- LISTA DE REPORTS -->
        <div
          v-if="!showReportForm"
          key="list"
          class="card shadow-sm border-0 mb-2"
        >
          <div class="card-body p-3">
            <!-- Loader inicial -->
            <div
              v-if="loading.bootstrap"
              class="text-muted small d-flex align-items-center"
            >
              <i class="bi bi-arrow-repeat me-2 spin"></i> Carregando reports...
            </div>

            <template v-else>
              <template v-if="hasReports">
                <b-list-group class="reports-list">
                  <b-list-group-item
                    v-for="(r, idx) in localReports"
                    :key="(r && r.id) || idx"
                    class="d-flex align-items-start justify-content-between"
                  >
                    <!-- contador à esquerda -->
                    <div class="me-3 d-flex align-items-start">
                      <b-badge variant="dark" pill class="report-counter">{{
                        idx + 1
                      }}</b-badge>
                    </div>

                    <div class="flex-grow-1 pe-3">
                      <div class="text-break">{{ (r && r.reason) || '—' }}</div>
                      <div class="mt-1 text-muted small">
                        <span v-if="r && r.createdAt"
                          >Em: {{ formatDate(r.createdAt) }}</span
                        >
                        <span v-if="r && r.user && r.user.name">
                          • Usuário: {{ r.user.name }}</span
                        >
                        <span v-if="r && r.store && r.store.name">
                          • Loja: {{ r.store.name }}</span
                        >
                      </div>
                    </div>

                    <div class="ms-2 d-flex align-items-start">
                      <b-button
                        v-if="canDeleteReport(r)"
                        size="sm"
                        variant="outline-danger"
                        title="Excluir report"
                        @click="onDeleteReport(r)"
                      >
                        <span class="fas fa-trash-alt text-danger"></span>
                      </b-button>
                    </div>
                  </b-list-group-item>
                </b-list-group>
              </template>

              <template v-else>
                <div class="text-muted small">
                  Nenhum report registrado para este cliente.
                </div>
              </template>
            </template>
          </div>
        </div>

        <!-- FORMULÁRIO DE NOVO REPORT -->
        <div v-else key="form" class="card shadow-sm border-0 mb-2">
          <div class="card-body p-3">
            <label class="form-label mb-1 fw-semibold">
              Motivo do report
              <small class="text-muted ms-1">(obrigatório)</small>
            </label>

            <b-form-textarea
              v-model.trim="reason"
              :maxlength="reasonMax"
              rows="3"
              placeholder="Descreva rapidamente o que aconteceu…"
            ></b-form-textarea>

            <div class="d-flex justify-content-between align-items-center mt-2">
              <small
                :class="{
                  'text-muted': remainingChars >= 0,
                  'text-danger': remainingChars < 0
                }"
              >
                {{ remainingChars }} caractere{{
                  Math.abs(remainingChars) === 1 ? '' : 's'
                }}
                {{ remainingChars >= 0 ? 'restantes' : 'excedidos' }}
              </small>

              <div class="d-flex gap-2">
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  @click="cancelReportForm"
                >
                  Cancelar
                </b-button>

                <b-button
                  size="sm"
                  variant="danger"
                  :disabled="!canConfirm || loading.create"
                  @click="confirmReport"
                >
                  <span v-if="loading.create">
                    <i class="bi bi-arrow-repeat me-1 spin"></i> Enviando…
                  </span>
                  <span v-else>
                    <i class="bi bi-send me-1"></i> Confirmar envio
                  </span>
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </b-modal>
</template>

<script>
import services from '@/services'
import { mapGetters } from 'vuex'

export default {
  name: 'CustomerInfoModal',
  props: {
    customer: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      required: true
    },
    // total absoluto (fallback); se "reports" vier, usamos reports.length
    reportsCount: {
      type: Number,
      default: 0
    },
    // lista de reports do cliente (opcional)
    // cada item pode ter { id, reason, createdAt, user?: {name}, store?: {name} }
    reports: {
      type: Array,
      default: () => []
    },
    // necessário para criar o report
    storeId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      lastTouch: {},
      showReportForm: false,
      showPointAdjustmentForm: false,
      showPointHistory: false,
      reason: '',
      reasonMax: 280,
      pointAdjustment: {
        mode: 'add',
        amount: 1,
        reason: '',
        loading: false,
        error: null
      },
      pointHistory: {
        items: [],
        loading: false,
        error: null
      },

      // dados locais (atualizados via requests)
      localCustomer: null,
      localReports: [],
      loading: {
        bootstrap: false,
        create: false
      }
    }
  },
  computed: {
    ...mapGetters('store', ['isAdmin']),
    // fonte de verdade do cliente
    customerView() {
      return this.localCustomer || this.customer || {}
    },
    totalPoints() {
      return (
        (this.customerView.points &&
          this.customerView.points.total_rents_closed) ||
        0
      )
    },
    totalPointsString() {
      return this.totalPoints.toString()
    },
    pointAdjustmentDelta() {
      const amount = Number(this.pointAdjustment.amount)
      if (!Number.isInteger(amount) || amount <= 0) return 0
      return this.pointAdjustment.mode === 'remove' ? -amount : amount
    },
    projectedPointsBalance() {
      return this.totalPoints + this.pointAdjustmentDelta
    },
    canConfirmPointAdjustment() {
      const reason = String(this.pointAdjustment.reason || '').trim()
      return Boolean(
        !this.pointAdjustment.loading &&
          this.pointAdjustmentDelta !== 0 &&
          Math.abs(this.pointAdjustmentDelta) <= 100 &&
          this.projectedPointsBalance >= 0 &&
          reason.length >= 5 &&
          reason.length <= 500
      )
    },

    // >>> NOVO: fonte única de verdade para render dos reports
    reportsView() {
      return this.localReports && this.localReports.length
        ? this.localReports
        : this.reports || []
    },
    computedReportsCount() {
      return this.reportsView.length || this.reportsCount || 0
    },
    hasReports() {
      return this.computedReportsCount > 0
    },
    remainingChars() {
      return this.reasonMax - (this.reason?.length || 0)
    },
    canConfirm() {
      return (
        this.reason && this.reason.trim().length > 0 && this.remainingChars >= 0
      )
    }
  },
  watch: {
    // Abriu o modal: refaz bootstrap
    visible(val) {
      if (val) this.bootstrapRequests()
    },
    // Mudou o cliente recebido: refaz bootstrap se o modal estiver aberto
    customer: {
      deep: true,
      handler() {
        if (this.visible) this.bootstrapRequests()
      }
    }
  },
  mounted() {
    if (this.visible) this.bootstrapRequests()
  },
  methods: {
    canDeleteReport(report) {
      if (!report) return false

      // Admin sempre pode
      if (this.isAdmin) return true

      // Não-admin: apenas nos primeiros 5 minutos do report
      const created = new Date(report.createdAt).getTime()
      if (!Number.isFinite(created)) return false

      const FIVE_MIN = 5 * 60 * 1000 // 5 minutos em ms
      const now = Date.now()
      return now - created <= FIVE_MIN
    },
    async onDeleteReport(report) {
      if (!this.canDeleteReport(report)) {
        this.$root.$toast?.error?.(
          'Você não tem permissão para excluir este report.'
        )
        return
      }
      if (!report?.id) return
      const message = `Deseja realmente excluir o report ?`
      const result = await this.$bvModal.msgBoxConfirm(message, {
        title: 'Confirmação',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Sim',
        cancelTitle: 'Não',
        centered: true
      })
      if (!result) {
        return
      }

      try {
        await services.customers.deleteCustomerReport(report.id)
        this.$root.$toast?.success?.('Report excluído.')
        // recarrega a lista
        const customerId = this.localCustomer?.id || this.customer?.id
        const { data } =
          await services.customers.fetchCustomerReportsByCustomerId(customerId)
        this.localReports = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : []
      } catch (err) {
        this.$root.$toast?.error?.('Não foi possível excluir o report.')
        // eslint-disable-next-line no-console
        console.error(err)
      }
    },
    async bootstrapRequests() {
      try {
        this.loading.bootstrap = true
        this.localReports = []
        this.localCustomer = null

        // Atualiza cliente pelo documento, se existir
        const doc = this.customer?.document
        if (doc) {
          try {
            const { data } = await services.customers.fetchCustomerByDocument(
              doc
            )
            // Ajuste conforme seu backend (data?.data ou data direto)
            const fetchedCustomer = data?.data || data || {}
            this.localCustomer = {
              ...this.customer,
              ...fetchedCustomer,
              points: fetchedCustomer.points || this.customer?.points
            }
          } catch {
            this.localCustomer = this.customer
          }
        } else {
          this.localCustomer = this.customer
        }

        // Buscar reports pelo customerId (se tivermos id)
        const customerId = this.localCustomer?.id || this.customer?.id
        if (customerId) {
          const { data } =
            await services.customers.fetchCustomerReportsByCustomerId(
              customerId
            )
          // Backend: { success, data: [...] }
          this.localReports = Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
            ? data
            : []
        }
      } catch (err) {
        this.$root.$toast?.error?.('Não foi possível carregar os reports.')
        // eslint-disable-next-line no-console
        console.error(err)
      } finally {
        this.loading.bootstrap = false
      }
    },

    onHide() {
      this.$emit('close')
      this.resetReportForm()
      this.cancelPointAdjustment()
      this.showPointHistory = false
    },
    copyToClipboard(text) {
      if (!text) {
        this.$root.$toast?.error?.('Sem informações para copiar.')
        return
      }
      navigator.clipboard
        .writeText(String(text))
        .then(() => {
          this.$root.$toast?.success?.('Informação copiada com sucesso!')
        })
        .catch((err) => {
          this.$root.$toast?.error?.('Ocorreu um problema ao copiar.')
          // eslint-disable-next-line no-console
          console.log(err)
        })
    },
    doubleTap(key, text) {
      const now = Date.now()
      if (this.lastTouch[key] && now - this.lastTouch[key] < 400) {
        this.copyToClipboard(text)
        this.lastTouch[key] = 0
      } else {
        this.lastTouch[key] = now
      }
    },
    toggleReportForm() {
      this.showReportForm = !this.showReportForm
      if (!this.showReportForm) this.resetReportForm()
    },
    cancelReportForm() {
      this.resetReportForm()
      this.showReportForm = false
    },
    resetReportForm() {
      this.reason = ''
    },

    startPointAdjustment(mode) {
      this.pointAdjustment = {
        mode,
        amount: 1,
        reason: '',
        loading: false,
        error: null
      }
      this.showPointAdjustmentForm = true
    },
    cancelPointAdjustment() {
      this.showPointAdjustmentForm = false
      this.pointAdjustment = {
        mode: 'add',
        amount: 1,
        reason: '',
        loading: false,
        error: null
      }
    },
    async confirmPointAdjustment() {
      if (!this.isAdmin || !this.canConfirmPointAdjustment) return

      const customerId = this.localCustomer?.id || this.customer?.id
      if (!customerId) {
        this.pointAdjustment.error = 'Cliente inválido.'
        return
      }

      try {
        this.pointAdjustment.loading = true
        this.pointAdjustment.error = null
        const { data } = await services.customers.adjustCustomerPoints({
          customerId,
          storeId: this.storeId,
          delta: this.pointAdjustmentDelta,
          reason: this.pointAdjustment.reason.trim()
        })
        const result = data?.data || data
        const points = result?.points

        if (!points) throw new Error('Resposta de pontuação inválida.')

        const baseCustomer = this.localCustomer || this.customer || {}
        this.localCustomer = { ...baseCustomer, points }
        this.$emit('points-adjusted', points)
        this.$root.$toast?.success?.('Pontuação ajustada com sucesso.')
        this.cancelPointAdjustment()

        if (this.showPointHistory) await this.loadPointHistory()
      } catch (error) {
        this.pointAdjustment.error =
          error?.response?.data?.message ||
          error?.message ||
          'Não foi possível ajustar a pontuação.'
      } finally {
        this.pointAdjustment.loading = false
      }
    },
    async togglePointHistory() {
      this.showPointHistory = !this.showPointHistory
      if (this.showPointHistory) await this.loadPointHistory()
    },
    async loadPointHistory() {
      const customerId = this.localCustomer?.id || this.customer?.id
      if (!this.isAdmin || !customerId) return

      try {
        this.pointHistory.loading = true
        this.pointHistory.error = null
        const { data } = await services.customers.fetchCustomerPointAdjustments(
          {
            customerId,
            storeId: this.storeId
          }
        )
        const result = data?.data || data || {}
        this.pointHistory.items = Array.isArray(result.adjustments)
          ? result.adjustments
          : []
      } catch (error) {
        this.pointHistory.error =
          error?.response?.data?.message ||
          'Não foi possível carregar o histórico de ajustes.'
      } finally {
        this.pointHistory.loading = false
      }
    },

    async confirmReport() {
      if (!this.canConfirm) return
      try {
        this.loading.create = true

        const customerId = this.localCustomer?.id || this.customer?.id
        if (!customerId) {
          this.$root.$toast?.error?.('Cliente inválido.')
          return
        }

        await services.customers.createCustomerReport({
          storeId: this.storeId,
          customerId,
          reason: this.reason.trim()
        })

        this.$root.$toast?.success?.('Report enviado com sucesso.')

        // Atualiza a lista de reports após criar
        const { data } =
          await services.customers.fetchCustomerReportsByCustomerId(customerId)
        this.localReports = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
          ? data
          : []

        // Reseta form e volta para a lista
        this.resetReportForm()
        this.showReportForm = false
      } catch (err) {
        this.$root.$toast?.error?.('Não foi possível enviar o report.')
        // eslint-disable-next-line no-console
        console.error(err)
      } finally {
        this.loading.create = false
      }
    },

    formatDate(dateStr) {
      try {
        const d = new Date(dateStr)
        return d.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch {
        return String(dateStr || '')
      }
    }
  }
}
</script>

<style scoped>
.info-tip {
  font-size: 0.9em;
  color: #6c757d;
  margin-bottom: 16px;
}

span {
  cursor: pointer;
  transition: color 0.2s;
}

span:hover {
  color: #007bff;
  text-decoration: underline;
}

.points-badge {
  display: inline-block;
  border-radius: 4px;
  margin-left: 4px;
}

.points-admin-panel,
.point-adjustment-form {
  border: 1px solid #dee2e6;
  border-radius: 0.35rem;
  padding: 0.75rem;
}

.points-admin-panel {
  background: #f8f9fa;
}

.point-adjustment-form {
  background: #fff;
}

.point-history {
  max-height: 280px;
  overflow-y: auto;
}

.point-history-item {
  font-size: 0.9rem;
}

/* Lista de reports com altura máxima e scroll */
.reports-list {
  max-height: 220px;
  overflow: auto;
}

/* Transição suave para trocar entre lista e form */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Spinner simples usando bootstrap icons */
.spin {
  display: inline-block;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Card estilo do mock */
.report-item {
  background: #f8f8f8; /* fundo claro, não preto */
  color: #4d4d4d; /* texto escuro */
  border: 2px solid #cacaca; /* borda escura estilo “giz” */
  border-radius: 12px;
  padding: 14px 16px;
}

/* Título central [Motivo] */
.report-item__header {
  text-align: center;
  font-weight: 700;
  margin-bottom: 6px;
  font-size: 1rem;
}

/* Linha divisória */
.report-item__divider {
  height: 2px;
  background: #222;
  width: 90%;
  margin: 0 auto 10px auto;
}

/* Texto “Motivo” centralizado */
.report-item__reason {
  text-align: center;
  font-size: 1rem;
  margin-bottom: 12px;
  font-weight: 500;
  word-break: break-word;
}

/* Lista Data / Onde / Usuario */
.report-item__meta li + li {
  margin-top: 4px;
}
.report-item__label {
  font-weight: 600;
  margin-right: 4px;
}
.report-item__value {
  color: #333;
}
</style>
